import React, { useContext } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { ADMIN_PERMISSIONS, SUPER_ADMIN_PERMISSIONS, ACCOUNTANT_PERMISSIONS, DONOR_PERMISSIONS } from "../data/roles";
import DashboardLayout from "../layouts/DashboardLayout";
import Login from "../pages/Login";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/Home";
import DonorDashboard from "../pages/home/DonorDashboard";
import ScholarshipApplicationForm from "../pages/scholarship-application-form/ScholarshipApplicationForm";
import ScholarshipApplications from "../pages/scholarship-applications/ScholarshipApplications";
import Users from "../pages/users/Users";
import { UserRolesEnum } from "../typings";
import PrivateRoute from "./PrivateRoutes";
import { ScholarshipReapplyForm } from "../pages";
import ForgotPassword from "../pages/ForgotPassword";
import Signup from "../pages/Signup";
import Accounts from "../pages/accounts/Accounts";
import ResetPassword from "../pages/reset-password";
import Transactions from "../pages/transactions/Transactions";
import ComingSoon from "../pages/ComingSoon";
import Settings from "../pages/settings/Settings";

export const AppRoutes = () => {
  const auth = useContext(AuthContext);
  const token = localStorage.getItem("token");

  if (!auth) return null; // Prevents crashes if context is unavailable

  const isAdmin = [...ADMIN_PERMISSIONS, ...SUPER_ADMIN_PERMISSIONS].includes(
    auth.role as UserRolesEnum
  );
  const isSuperAdmin = [...SUPER_ADMIN_PERMISSIONS].includes(
    auth.role as UserRolesEnum
  );
  const isAccountant = [...ACCOUNTANT_PERMISSIONS].includes(
    auth.role as UserRolesEnum
  );
  const isDonor = [...DONOR_PERMISSIONS].includes(
    auth.role as UserRolesEnum
  );
  // Exact role check — true ONLY when the user is specifically a Donor
  const isDonorOnly = auth.role === UserRolesEnum.DONOR;

  return (
    <Router>
      <Routes>
        {!token ? (
          // If no token, only allow access to login
          <>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="*" element={<Navigate to="/login" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </>
        ) : (
          // If token is present, enforce role-based access
          <>
            <Route path="/login" element={<Navigate to="/" />} />
            <Route element={<PrivateRoute />}>
              <Route element={<DashboardLayout />}>
                {isAdmin ? (
                  // Show admin routes
                  <>
                    <Route path="/" element={<Navigate to="/home" />} />
                    <Route path="/home" element={<Home />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/accounts" element={<Accounts />} />
                    <Route path="/transactions" element={<Transactions />} />
                  </>
                ) : isAccountant ? (
                  // Show accountant routes (limited to financial modules)
                  <>
                    <Route path="/" element={<Navigate to="/applications" />} />
                    <Route path="/accounts" element={<Accounts />} />
                    <Route path="/transactions" element={<Transactions />} />
                  </>
                ) : isDonorOnly ? (
                  // Show donor dashboard
                  <>
                    <Route path="/" element={<Navigate to="/home" />} />
                    <Route path="/home" element={<DonorDashboard />} />
                  </>
                ) : (
                  // Show limited access routes for other roles
                  <Route path="/" element={<Navigate to="/applications" />} />
                )}
                <Route
                  path="/applications"
                  element={isDonorOnly ? <Navigate to="/home" /> : <ScholarshipApplications />}
                />
                <Route
                  path="/settings"
                  element={isSuperAdmin ? <Settings /> : <Navigate to="/" />}
                />
                <Route path="/help" element={<ComingSoon />} />
              </Route>
              <Route
                path="/scholarship-form"
                element={
                  <ScholarshipApplicationForm />
                }
              />
              <Route
                path="/scholarship-form/:id"
                element={
                  <ScholarshipApplicationForm />
                }
              />
              <Route
                path="/scholarship-reapply/:parent_application_id"
                element={
                  <ScholarshipReapplyForm />
                }
              />
            </Route>
            <Route path="*" element={<NotFound />} />
          </>
        )}
      </Routes>
    </Router>
  );
};
